import styles from './VersionControl.module.css';
import { useState, useEffect, useRef } from 'react';
import {
    OptimizedMotionH1,
    OptimizedMotion,
} from '../../../shared/ui/optimized-motion/OptimizedMotion';
import ReadMoreButton from './ReadMoreButton';
import { useNavigate } from 'react-router-dom';

import image1 from './images/Group 1.svg';
import image2 from './images/Group 2 .svg';
import image3 from './images/Code field.svg';

const VersionControl = () => {
    const navigate = useNavigate();
    const [currentTab, setCurrentTab] = useState(0);
    const [isScrolling, setIsScrolling] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const lastScrollTime = useRef(0);

    const tabs = [
        {
            id: 1,
            title: 'One source of truth',
            description:
                "Say goodbye to 'final_v7_reallyFINAL.' All drafts, comments, and assets are stored in one single, time-stamped thread. Everyone always knows what's current—and where each change came from.",
            image: image1,
        },
        {
            id: 2,
            title: 'Instant traceability',
            description:
                'Track changes with precision. View side-by-side differences, see who made edits, and read approval notes with timestamps. Roll back or branch versions instantly without losing progress.',
            image: image2,
        },
        {
            id: 3,
            title: 'Faster iterations, fewer mistakes',
            description:
                'Real-time conflict detection prevents overwrite errors. Combine this with AI-powered suggestions and your team can iterate faster—with less human error and firmer launch timelines.',
            image: image3,
        },
    ];

    useEffect(() => {
        const handleWheel = (e: WheelEvent) => {
            if (!containerRef.current) return;

            const now = Date.now();
            if (now - lastScrollTime.current < 100) return;

            const rect = containerRef.current.getBoundingClientRect();
            const isInView = rect.top <= window.innerHeight && rect.bottom >= 0;

            if (isInView && !isScrolling) {
                if (e.deltaY > 0) {
                    if (currentTab < tabs.length - 1) {
                        e.preventDefault();
                        setIsScrolling(true);
                        setCurrentTab((prev) => prev + 1);
                        lastScrollTime.current = now;
                        setTimeout(() => setIsScrolling(false), 800);
                    }
                } else if (e.deltaY < 0) {
                    if (currentTab > 0) {
                        e.preventDefault();
                        setIsScrolling(true);
                        setCurrentTab((prev) => prev - 1);
                        lastScrollTime.current = now;
                        setTimeout(() => setIsScrolling(false), 800);
                    }
                }
            }
        };

        window.addEventListener('wheel', handleWheel, { passive: false });
        return () => window.removeEventListener('wheel', handleWheel);
    }, [currentTab, isScrolling, tabs.length]);

    return (
        <div ref={containerRef} className={styles.container}>
            <div className={styles.content}>
                <div className={styles.leftSide}>
                    <OptimizedMotionH1 className={styles.title} type="slide-down" delay={0.2}>
                        <span className={styles.titleHighlight}>Version</span>
                        <span className={styles.wrenchIcon}>📂</span>
                        <span className={styles.titleText}>control</span>
                    </OptimizedMotionH1>

                    <div className={styles.tabContent}>
                        <h3 className={styles.featureTitle}>{tabs[currentTab].title}</h3>
                        <p className={styles.featureDescription}>{tabs[currentTab].description}</p>

                        <div className={styles.readMoreButtonWrapper}>
                            <ReadMoreButton
                                onClick={() => {
                                    navigate('/version-control');
                                }}
                            />
                        </div>
                    </div>

                    <div className={styles.tabIndicators}>
                        {tabs.map((_, index) => (
                            <div
                                key={index}
                                className={`${styles.indicator} ${
                                    index === currentTab ? styles.indicatorActive : ''
                                }`}
                                data-number={index + 1}
                                onClick={() => setCurrentTab(index)}
                            />
                        ))}
                    </div>
                </div>

                <div className={styles.rightSide}>
                    <div className={styles.imageContainer}>
                        {tabs.map((tab, index) => (
                            <img
                                key={tab.id}
                                src={tab.image}
                                alt={tab.title}
                                className={`${styles.tabImage} ${
                                    index === currentTab ? styles.tabImageActive : ''
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VersionControl;
